import { Component, useCallback, useMemo, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCollectionRecommendations,
  useAddToCollection,
  COLLECTION_KEYS,
} from "@/hooks/useCollections";
import { SectionHeader } from "@/components/shared/SectionHeader";
import {
  StashThumbnailList,
} from "@/components/shared/StashThumbnail/StashThumbnailList";
import type { StashThumbnailData } from "@/components/shared/StashThumbnail/StashThumbnail";
import type { RecommendedItem, RecommendationsResponse } from "@/types/collections";

// ---------------------------------------------------------------------------
// Error boundary — crashes render nothing, rest of the page is unaffected
// ---------------------------------------------------------------------------

class RecommendationsErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error("[CollectionRecommendations] Section crashed:", error);
  }
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Map API data → StashThumbnailData
// ---------------------------------------------------------------------------

export function mapRecommendedToThumbnail(item: RecommendedItem): StashThumbnailData {
  const d = item.data as Record<string, unknown>;
  return {
    id: item.stasht_id,
    title: (d.title as string) ?? undefined,
    user_title: (d.user_title as string) ?? undefined,
    content: d.content as StashThumbnailData["content"],
    platform: (d.platform as string) ?? null,
    canonical_url: (d.canonical_url as string) ?? null,
    enrichment_status: (d.enrichment_status as string) ?? undefined,
    main_enrichment_response: d.main_enrichment_response as
      | { title?: string }
      | undefined,
  };
}

// ---------------------------------------------------------------------------
// Inner component (unwrapped from error boundary)
// ---------------------------------------------------------------------------

function CollectionRecommendationsInner({
  collectionId,
  className,
}: {
  collectionId: string;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useCollectionRecommendations(collectionId);
  const addMutation = useAddToCollection();

  const thumbnails = useMemo(() => {
    if (!data?.items?.length) return [];
    return data.items.map(mapRecommendedToThumbnail);
  }, [data]);

  const handleAdd = useCallback(
    (stashtId: string) => {
      // Optimistically remove from local recommendations
      queryClient.setQueryData<RecommendationsResponse>(
        COLLECTION_KEYS.recommendations(collectionId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.filter((i) => i.stasht_id !== stashtId),
          };
        },
      );

      addMutation.mutate(
        { collectionId, items: [{ type: "stash", id: stashtId }] },
        {
          onSuccess: () => toast.success("Added to collection"),
          onError: () => {
            toast.error("Failed to add");
            // Revert optimistic removal
            queryClient.invalidateQueries({
              queryKey: COLLECTION_KEYS.recommendations(collectionId),
            });
          },
        },
      );
    },
    [collectionId, addMutation, queryClient],
  );

  const getOverlay = useCallback(
    (post: StashThumbnailData) => (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleAdd(post.id);
        }}
        className="absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm active:scale-95"
        aria-label="Add to collection"
      >
        <Plus className="h-4 w-4" />
      </button>
    ),
    [handleAdd],
  );

  if (isLoading) return null;
  if (!thumbnails.length) return null;

  return (
    <div className={className}>
      <SectionHeader
        title="Recommended"
      />
      <div className="relative">
        <StashThumbnailList
          posts={thumbnails}
          maxItems={20}
          disablePadding
          getItemOverlay={getOverlay}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public export — wrapped in error boundary
// ---------------------------------------------------------------------------

export function CollectionRecommendations({
  collectionId,
  className,
}: {
  collectionId: string;
  className?: string;
}) {
  return (
    <RecommendationsErrorBoundary>
      <CollectionRecommendationsInner
        collectionId={collectionId}
        className={className}
      />
    </RecommendationsErrorBoundary>
  );
}
